import {Line} from 'vue-chartjs';
import Chart from 'chart.js';
import _ from 'lodash';
import './workaround';

export default {
    name: 'TrackerGraph',
    extends: Line,
    data: () => {
        return {
            chartOptions: {
                animation: false,
                title: {
                    display: false
                },
                legend: {
                    display: false
                },
                tooltips: {
                    enabled: true,
                    mode: 'index',
                    axis: 'x',
                    intersect: false,
                    yAlign: 'center',
                    yAlignForced: true, // implemented by workaround.js
                    position: 'TrackerGraphTooltipPositioner',
                    xPadding: 24,
                    yPadding: 24,
                    BackgroundColor: 'rgba(0,0,0,0.9)',
                    titleFontFamily: 'Lato',
                    titleFontSize: 12,
                    titleFontWeight: 'regular',
                    titleSpacing: 20,
                    titleFontColor: '#ccc',
                    titleMarginBottom: 16,
                    bodyFontSize: 16,
                    bodyFontColor: '#fff',
                    bodyFontFamily: 'Lato',
                    caretSize: 16,
                    caretPadding: 8,
                    cornerRadius: 3,
                    bodySpacing: 10
                },
                scales: {
                    yAxes: [{
                        type: 'linear',
                        ticks: {
                            beginAtZero: true,
                            min: 0,
                            suggestedMax: 1
                        },
                        scaleLabel: {},
                    }],
                    xAxes: [{
                        type: 'time',
                        ticks: {
                            autoSkip: true,
                            maxTicksLimit: 11,
                        },
                        scaleLabel: {},
                        time: {
                            unit: 'day', // Automatically set to month when range surpasses this.monthThreshold
                            tooltipFormat: 'll' // Localized format for showing day, month name (short) and year
                        },
                        gridLines: {
                            display: false
                        },
                    }]
                },
                elements: {
                    line: {
                        borderWidth: 2,
                        fill: false,
                        tension: 0,
                    },
                    point: {
                        borderWidth: 0,
                        hitRadius: 2,
                        radius: 0,
                    },
                },
            },
            tracker: {
                color: '#757575',
                width: 4
            },
        };
    },
    props: {
        chartData: {type: Object, default: () => ({datasets: [], labels: []})},
        step: {type: String, default: 'day'},
        value: {type: Date, default: null},
        labelXaxis: {type: String, default: ''},
        labelYaxis: {type: String, default: ''},
    },
    computed: {
        reactiveHidden() {
            // This computed property creates to changes on any dataset's .hidden property to update the graph
            return this.chartData.datasets.map(dataset => dataset.hidden);
        },
    },
    model: {
        prop: 'value',
        event: 'input',
    },
    watch: {
        chartData: 'updateChart',
        step: 'updateChart',
        reactiveHidden: 'updateChart',
        labelXaxis: 'updateChart',
        labelYaxis: 'updateChart',
    },
    mounted() {
        // Store reference because the plugin gets a different `this`.
        const vue_component = this;
        // Plugin: Vertical tracker bar renderer
        this.addPlugin({
            afterDraw(chart) {
                // If a tooltip is being drawn, then draw a vertical line at the tooltip x position.
                let shouldDraw = false;
                const tracker = vue_component.tracker;
                if (vue_component.value) {
                    tracker.x = chart.scales['x-axis-0'].getPixelForValue(vue_component.value);
                    shouldDraw = true;
                }
                if (!shouldDraw && chart.tooltip._active && chart.tooltip._active.length) {
                    tracker.x = chart.tooltip._active[0].tooltipPosition().x;
                    shouldDraw = true;
                }
                if (shouldDraw) {
                    const ctx = chart.ctx;
                    const topY = chart.scales['y-axis-0'].top;
                    const bottomY = chart.scales['y-axis-0'].bottom;
                    const x = tracker.x;
                    // Render line
                    ctx.save();
                    ctx.beginPath();
                    ctx.moveTo(x, topY);
                    ctx.lineTo(x, bottomY);
                    ctx.lineWidth = tracker.width;
                    ctx.strokeStyle = tracker.color;
                    ctx.stroke();
                    ctx.restore();
                }
            },
            afterEvent(chart, e) {
                // Emit the click event passing through the clicked date value
                if (!(e.type === 'click' || e.type === 'mouseup')) {
                    return;
                }
                const tooltip = chart.tooltip._active[0];
                const index = tooltip._index;
                const date = chart.data.labels[index];
                vue_component.$emit('input', date);
            }
        });
        // Plugin: Force tooltip on highlight
        // Based on: https://stackoverflow.com/questions/36992922/chart-js-v2-how-to-make-tooltips-always-appear-on-pie-chart
        this.addPlugin({
            beforeRender: function (chart) {
                chart.pluginTooltips = [];
                if (vue_component.value) {
                    // Find the index which is being highlighted and build a tooltip which shows for all values across the datasets for that index
                    const valueIndex = _.findIndex(chart.data.labels, item => item.getTime() === vue_component.value.getTime());
                    chart.pluginTooltips.push(new Chart.Tooltip({
                        _chart: chart.chart,
                        _chartInstance: chart,
                        _data: chart.data,
                        _options: chart.options.tooltips,
                        _active: chart.config.data.datasets.map((dataset, i) => chart.getDatasetMeta(i).data[valueIndex])
                    }, chart));
                    // Turn off normal tooltips
                    chart.options.tooltips.enabled = false;
                } else {
                    // If we are not forcing the tooltip then re-enable the default tooltip behaviour
                    chart.options.tooltips.enabled = true;
                }
            },
            afterDraw: function (chart, easing) {
                if (vue_component.value) {
                    // We don't want the permanent tooltips to animate, so don't do anything till the animation runs atleast once
                    if (!chart.allTooltipsOnce) {
                        if (easing !== 1)
                            return;
                        chart.allTooltipsOnce = true;
                    }

                    // Draw the tooltips
                    chart.options.tooltips.enabled = true;
                    Chart.helpers.each(chart.pluginTooltips, function (tooltip) {
                        tooltip.initialize();
                        tooltip.update();
                        // We don't actually need this since we are not animating tooltips
                        tooltip.pivot();
                        tooltip.transition(easing).draw();
                    });
                    chart.options.tooltips.enabled = false;
                }
            }
        });
        this.updateChart();
    },
    methods: {
        updateChart() {
            this.updateChartOptions();
            this.renderChart(this.chartData, this.chartOptions);
        },
        updateChartOptions() {
            this.chartOptions.scales.xAxes[0].scaleLabel.display = !!(this.labelXaxis);
            this.chartOptions.scales.xAxes[0].scaleLabel.labelString = this.labelXaxis;
            this.chartOptions.scales.yAxes[0].scaleLabel.display = !!(this.labelYaxis);
            this.chartOptions.scales.yAxes[0].scaleLabel.labelString = this.labelYaxis;
            this.chartOptions.scales.xAxes[0].time.unit = this.step;
        }
    }
};

Chart.Tooltip.positioners.TrackerGraphTooltipPositioner = function (elements) {
    // Because the tooltip scrolls over the x-axis all elements are vertically aligned.
    // For some reason we cant use eventPos.x as it produces the wrong values.
    const element = elements[0];
    const x = element.tooltipPosition().x;
    return {
        x: x,
        y: (this._chart.chartArea.bottom - this._chart.chartArea.top) / 2,
    };
};



// WEBPACK FOOTER //
// ./src/components/widgets/tracker-graph/TrackerGraph.js